import { UserApi } from "@/api/user.api";
import DashboardErrorPage from "@/components/DashboardErrorPage/DashboardErrorPage";
import PaymentStatus from "@/components/PaymentStatus/PaymentStatus";
import AppLayout from "@/layouts/AppLayout/AppLayout";
import RootLayout from "@/layouts/RootLayout/RootLayout";
import { AuthCallback } from "@/pages/AuthCallback/AuthCallback";
import { Checkout } from "@/pages/Checkout/Checkout";
import checkoutLoader from "@/pages/Checkout/checkoutLoader";
import ShortLink, { shortLinkLoader } from "@/pages/ShortLink/ShortLink";
import dashboardCartLoader from "@/pages/DashboardCart/dashboardCartLoader";
import dashboardCartsLoader from "@/pages/DashboardCarts/dashboardCartsLoader";
import dashboardCustomersLoader from "@/pages/DashboardCustomers/dashboardCustomersLoader";
import dashboardLineItemEditLoader from "@/pages/DashboardLineItemEdit/dashboardLineItemEditLoader";
import dashboardMasterlistLoader from "@/pages/DashboardMasterlist/dashboardMasterlistLoader";
import dashboardMasterlistGroupLoader from "@/pages/DashboardMasterlistGroup/dashboardMasterlistGroupLoader";
import dashboardOrdersLoader from "@/pages/DashboardOrders/dashboardOrdersLoader";
import dashboardPricingLoader from "@/pages/DashboardPricing/dashboardPricingLoader";
import dashboardPromocodesLoader from "@/pages/DashboardPromocodes/dashboardPromocodesLoader";
import dashboardShippingsLoader from "@/pages/DashboardShippings/dashboardShippingsLoader";
import adminProfileDetailsLoader from "@/pages/ProfileDetails/adminProfileDetailsLoader";
import profileLoader from "@/pages/ProfileDetails/profileDetailsLoader";
import adminProfileOrdersLoader from "@/pages/ProfileOrders/adminProfileOrdersLoader";
import profileOrdersLoader from "@/pages/ProfileOrders/profileOrdersLoader";
import adminProfilePasswordLoader from "@/pages/ProfilePassword/adminProfilePasswordLoader";
import profilePasswordLoader from "@/pages/ProfilePassword/profilePasswordLoader";
import adminProfileUploadsLoader from "@/pages/ProfileUploads/adminProfileUploadsLoader";
import profileUploadsLoader from "@/pages/ProfileUploads/profileUploadsLoader";
import { Upload } from "@/pages/Upload/Upload";
import { NotFound } from "@/pages/NotFound/NotFound";
import { $account } from "@/stores/account";
import { pushPageView } from "@/utils/analytics";
import { IS_INTERNAL } from "@/utils/constants";
import { toast } from "@/utils/toast";
import { useStore } from "@nanostores/react";
import { lazy, Suspense } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  redirect,
  Route,
} from "react-router-dom";

// PERF — route code-split (2026-07-06): the Visualizer (3D engine), the whole
// Dashboard/admin area, Profile pages, and the dev-only Test page are now
// lazy-loaded, so a first visit to /upload or /checkout no longer downloads
// them. Upload / Checkout / Payment / AuthCallback stay EAGER on purpose
// (checkout path must never wait on a chunk). Route loaders stay static
// imports — they're thin data fetchers, and react-router can run them while
// the element chunk is still downloading.
const Visualizer = lazy(() =>
  import("@/pages/Visualizer/Visualizer").then(m => ({
    default: m.Visualizer,
  }))
);
const DashboardLayout = lazy(
  () => import("@/layouts/DashboardLayout/DashboardLayout")
);
const ProfileLayout = lazy(
  () => import("@/layouts/ProfileLayout/ProfileLayout")
);
const DashboardCart = lazy(
  () => import("@/pages/DashboardCart/DashboardCart")
);
const DashboardCarts = lazy(
  () => import("@/pages/DashboardCarts/DashboardCarts")
);
const DashboardCustomers = lazy(
  () => import("@/pages/DashboardCustomers/DashboardCustomers")
);
const DashboardLineItemAdd = lazy(
  () => import("@/pages/DashboardLineItemAdd/DashboardLineItemAdd")
);
const DashboardLineItemEdit = lazy(
  () => import("@/pages/DashboardLineItemEdit/DashboardLineItemEdit")
);
const DashboardMasterlist = lazy(
  () => import("@/pages/DashboardMasterlist/DashboardMasterlist")
);
const DashboardMasterlistGroup = lazy(
  () => import("@/pages/DashboardMasterlistGroup/DashboardMasterlistGroup")
);
const DashboardMasterlistSettings = lazy(
  () =>
    import("@/pages/DashboardMasterlistSettings/DashboardMasterlistSettings")
);
const DashboardMessages = lazy(
  () => import("@/pages/DashboardMessages/DashboardMessages")
);
const DashboardOrders = lazy(
  () => import("@/pages/DashboardOrders/DashboardOrders")
);
const DashboardIntlShipping = lazy(
  () => import("@/pages/DashboardIntlShipping/DashboardIntlShipping")
);
const DashboardCutBatch = lazy(
  () => import("@/pages/DashboardCutBatch/DashboardCutBatch")
);
const DashboardClaude = lazy(
  () => import("@/pages/DashboardClaude/DashboardClaude")
);
const DashboardSupplies = lazy(
  () => import("@/pages/DashboardSupplies/DashboardSupplies")
);
const DashboardPricing = lazy(
  () => import("@/pages/DashboardPricing/DashboardPricing")
);
const DashboardPromocodes = lazy(
  () => import("@/pages/DashboardPromocodes/DashboardPromocodes")
);
const DashboardShippings = lazy(
  () => import("@/pages/DashboardShippings/DashboardShippings")
);
const DashboardTools = lazy(
  () => import("@/pages/DashboardTools/DashboardTools")
);
const DashboardUploads = lazy(
  () => import("@/pages/DashboardUploads/DashboardUploads")
);
// Staff module (2026-09-02): hours / time off / team. One shell loads the
// shared data; the pages are children so the sub-tab strip stays put.
const StaffShell = lazy(() => import("@/pages/DashboardStaff/StaffShell"));
const StaffPicker = lazy(() => import("@/pages/DashboardStaff/StaffPicker"));
const StaffHours = lazy(() => import("@/pages/DashboardStaff/StaffHours"));
const StaffTimeOff = lazy(
  () => import("@/pages/DashboardStaff/StaffTimeOff")
);
const StaffTeam = lazy(() => import("@/pages/DashboardStaff/StaffTeam"));
const StaffGuidelines = lazy(
  () => import("@/pages/DashboardStaff/StaffGuidelines")
);
const ProfileDetails = lazy(
  () => import("@/pages/ProfileDetails/ProfileDetails")
);
const ProfileOrders = lazy(
  () => import("@/pages/ProfileOrders/ProfileOrders")
);
const ProfilePassword = lazy(
  () => import("@/pages/ProfilePassword/ProfilePassword")
);
const ProfilePaymentMethods = lazy(
  () => import("@/pages/ProfilePaymentMethods/ProfilePaymentMethods")
);
const ProfileUploads = lazy(
  () => import("@/pages/ProfileUploads/ProfileUploads")
);
const Test = lazy(() => import("@/pages/Test/Test"));

// Every lazy element needs a Suspense boundary; fallback is intentionally
// blank — these pages have their own internal loading states once mounted.
const s = (node: React.ReactNode) => (
  <Suspense fallback={null}>{node}</Suspense>
);

function ProtectedRoute({ redirect = "/" }) {
  const account = useStore($account);
  if (!account.isLoggedIn) return <Navigate to={redirect} replace />;
  return <Outlet />;
}

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route path="/" element={<RootLayout />}>
      <Route path="*" element={<NotFound />} />
      <Route index element={<Navigate to="/upload" />} />

      <Route element={<AppLayout />}>
        <Route path="upload" element={<Upload />} />
        <Route path="visualizer" element={s(<Visualizer />)} />

        {/* Logged in routes */}
        <Route element={<ProtectedRoute redirect="/" />}>
          <Route path="profile" element={s(<ProfileLayout />)}>
            <Route index element={<Navigate to="details" />} />
            <Route
              path="details"
              index
              element={s(<ProfileDetails />)}
              loader={profileLoader}
            />
            <Route
              path="orders"
              element={s(<ProfileOrders />)}
              loader={profileOrdersLoader}
            />
            <Route
              path="uploads"
              element={s(<ProfileUploads />)}
              loader={profileUploadsLoader}
            />
            <Route
              path="password"
              element={s(<ProfilePassword />)}
              loader={profilePasswordLoader}
            />
            {/* Saved cards (staging 2026-08-25). Customer-mode only — no
                admin-mode twin: the wallet endpoints act on the CALLER's
                Stripe Customer, so an admin would see their own cards. */}
            <Route
              path="payment-methods"
              element={s(<ProfilePaymentMethods />)}
            />
          </Route>
        </Route>
      </Route>

      <Route path="auth/callback" element={<AuthCallback />} />
      <Route path="payment/success" element={<PaymentStatus />} />
      <Route path="payment/cancel" element={<PaymentStatus />} />
      <Route path="checkout" element={<Checkout />} loader={checkoutLoader} />
      <Route path="c/:code" element={<ShortLink />} loader={shortLinkLoader} />

      {/* Dashboard routes */}
      <Route
        path="dashboard"
        element={s(<DashboardLayout />)}
        errorElement={<DashboardErrorPage />}
        loader={async () => {
          if (IS_INTERNAL) return;
          function handleAuthError(redirectUrl: string) {
            toast.show({
              message: `You must be logged in as an admin to view this page.`,
              color: "red",
            });
            throw redirect(redirectUrl);
          }

          if (!$account.get().isLoggedIn) handleAuthError("/upload?login");
          const user = await UserApi.getUser();
          if (!user?.data.user?.isAdmin) handleAuthError("/upload");
          return;
        }}
      >
        <Route index element={<Navigate to="orders" />} />
        <Route
          path="orders"
          element={s(<DashboardOrders />)}
          loader={dashboardOrdersLoader}
        />
        <Route
          path="masterlist"
          element={s(<DashboardMasterlist />)}
          loader={dashboardMasterlistLoader}
        />
        <Route
          path="masterlist/group/:groupId"
          element={s(<DashboardMasterlistGroup />)}
          loader={dashboardMasterlistGroupLoader}
        />
        <Route
          path="masterlist-settings"
          element={s(<DashboardMasterlistSettings />)}
        />
        <Route
          path="customers"
          element={s(<DashboardCustomers />)}
          loader={dashboardCustomersLoader}
        />
        <Route
          path="pricing"
          element={s(<DashboardPricing />)}
          loader={dashboardPricingLoader}
        />
        <Route
          path="carts"
          element={s(<DashboardCarts />)}
          loader={dashboardCartsLoader}
        />
        <Route
          id="dashboard-cart"
          path="carts/:cartId"
          element={s(<DashboardCart />)}
          loader={dashboardCartLoader}
        >
          <Route
            path="line-items/add"
            element={s(<DashboardLineItemAdd />)}
          />
          <Route
            path="line-items/:lineItemId"
            element={s(<DashboardLineItemEdit />)}
            loader={dashboardLineItemEditLoader}
          />
        </Route>
        <Route
          path="shippings"
          element={s(<DashboardShippings />)}
          loader={dashboardShippingsLoader}
        />
        <Route
          path="promocodes"
          element={s(<DashboardPromocodes />)}
          loader={dashboardPromocodesLoader}
        />
        <Route path="uploads" element={s(<DashboardUploads />)} />
        {/* Tools (2026-07-24): staff tools embedded from Railway — no loader,
            the page is a static shell around the iframes. */}
        <Route path="tools" element={s(<DashboardTools />)} />
        {/* International label buying (2026-07-28). No loader: the page holds
            no order data — the destination, package and customs items are all
            typed by staff, and nothing is written to `orders`. */}
        <Route path="intl-shipping" element={s(<DashboardIntlShipping />)} />
        {/* Cut Batch (2026-09-05): read-only, no loader — job numbers are typed
            and the list is rendered by POST /api/cut-batch. */}
        <Route path="cut-batch" element={s(<DashboardCutBatch />)} />
        {/* Claude (2026-09-09): CS assistant — paste a thread, get the lane +
            reply. No loader; POST /api/claude/chat, read-only. */}
        <Route path="claude" element={s(<DashboardClaude />)} />
        {/* Supplies (2026-09-16): master supply list + purchase orders. No
            loader; the catalogue is a constant, nothing is written. */}
        <Route path="supplies" element={s(<DashboardSupplies />)} />
        <Route path="messages" element={s(<DashboardMessages />)} />
        {/* Staff (2026-09-02). "team" is declared before ":slug" so the
            static segment wins; a person literally named "team" would need a
            different slug. */}
        <Route path="staff" element={s(<StaffShell />)}>
          <Route index element={s(<StaffPicker />)} />
          <Route path="manager" element={s(<StaffTeam />)} />
          <Route path="team" element={<Navigate to="/dashboard/staff/manager" replace />} />
          <Route path="calendar" element={s(<StaffTimeOff />)} />
          {/* Guidelines (2026-09-08): static PDF embed, moved from Tools. */}
          <Route path="guidelines" element={s(<StaffGuidelines />)} />
          <Route path=":slug" element={s(<StaffHours />)} />
          <Route path=":slug/timeoff" element={s(<StaffTimeOff />)} />
        </Route>
        {/* Admin user profile routes */}
        <Route path="customers/:userId" element={s(<ProfileLayout />)}>
          <Route index element={<Navigate to="details" />} />
          <Route
            path="details"
            element={s(<ProfileDetails />)}
            loader={adminProfileDetailsLoader}
          />
          <Route
            path="orders"
            element={s(<ProfileOrders />)}
            loader={adminProfileOrdersLoader}
          />
          <Route
            path="uploads"
            element={s(<ProfileUploads />)}
            loader={adminProfileUploadsLoader}
          />
          <Route
            path="password"
            element={s(<ProfilePassword />)}
            loader={adminProfilePasswordLoader}
          />
        </Route>
      </Route>

      {process.env.NODE_ENV === "development" && (
        <Route path="test" element={s(<Test />)} />
      )}
    </Route>
  )
);

// GA4 / GTM SPA pageviews (WebFX, 2026-08-31): React Router doesn't fire
// native page loads on navigation, so push a page_view into the dataLayer on
// every location change. The INITIAL load is intentionally not pushed here —
// GTM's own container load covers it; pushing it again would double-count.
let lastTrackedPath = window.location.pathname + window.location.search;
router.subscribe(state => {
  const path = state.location.pathname + state.location.search;
  if (path !== lastTrackedPath) {
    lastTrackedPath = path;
    pushPageView(path);
  }
});
